import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BaseComponent } from '@macs-frontend/core';
import { ListsUrls } from '@macs-frontend/core/utils/consts';
import { makeObservable } from '@macs-frontend/core/utils/mobx';
import { function as F, option as O, readonlyRecord as RR } from 'fp-ts';
import { comparer, toJS } from 'mobx';
import { action, observable } from 'mobx-angular';
import { DeepReadonly } from 'ts-essentials';
import { DistanceUtils } from '../../utils/distance';

type ngClass = DeepReadonly<
  | string
  | string[]
  | Set<string>
  | {
      [klass: string]: any;
    }
>;

export enum SearchFilterType {
  Dropdown = 'dropdown',
  DropdownMultiple = 'dropdown-multiple',
  DropdownExternal = 'dropdown-external',
  DropdownExternalMultiple = 'dropdown-external-multiple',
  LocationRange = 'location-range',
  Range = 'range',
}

interface DropdownFilter {
  readonly type: SearchFilterType.Dropdown;
  readonly label?: string;
  readonly initialValue?: any;
  readonly items: readonly { readonly label?: string; readonly value: any }[];
  readonly multiple?: false;
  readonly canClear?: boolean /* default false */;
  readonly dropdownClasses?: ngClass /* default 'dropdown_default' */;
}

type DropdownMultipleFilter = Omit<
  DropdownFilter,
  'type' | 'multiple' | 'initialValue'
> & {
  readonly type: SearchFilterType.DropdownMultiple;
  readonly initialValue?: readonly any[];
};

type DropdownExternalFilter = Omit<DropdownFilter, 'type' | 'items' | 'initialValue'> & {
  readonly type: SearchFilterType.DropdownExternal;
  readonly url: DeepReadonly<ListsUrls>;
  readonly labelKey?: string;
  readonly valueKey?: string;
  readonly canAdd?: boolean /* default false */;
  readonly initialValue: any;
};

type DropdownExternalMultipleFilter = Omit<
  DropdownExternalFilter,
  'type' | 'multiple' | 'initialValue'
> & {
  readonly type: SearchFilterType.DropdownExternalMultiple;
  readonly initialValue: readonly any[];
};

interface LocationRangeFilterItem {
  readonly label?: string;
  readonly initialValue?: { readonly from?: number; readonly to?: number };
}

interface LocationRangeFilter {
  readonly type: SearchFilterType.LocationRange;
  readonly label?: string;
  readonly minLabel?: string /* default 'min' */;
  readonly maxLabel?: string /* default 'max' */;
  readonly measuringSystemLabel?: string /* default 'km' */;
  readonly items: {
    readonly [key: string]: LocationRangeFilterItem;
  };
}

interface RangeFilter {
  readonly type: SearchFilterType.Range;
  readonly label?: string;
  readonly min: number;
  readonly max: number;
  readonly initialValue?: { readonly min?: number; readonly max?: number };
}

type SearchFilters =
  | DropdownFilter
  | DropdownMultipleFilter
  | LocationRangeFilter
  | DropdownExternalFilter
  | DropdownExternalMultipleFilter
  | RangeFilter;

export interface SearchFilterItemsInput {
  readonly [key: string]: SearchFilters;
}

type ExtractDropdownType<T extends DropdownFilter> =
  | (T['items'] extends readonly {
      readonly value: infer U;
    }[]
      ? U
      : never)
  | (T extends { readonly initialValue: any } ? T['initialValue'] : undefined)
  | (T extends { readonly canClear: true } ? undefined : never);

type ExtractDropdownMultipleType<T extends DropdownMultipleFilter> = readonly [
  | (T['items'] extends readonly {
      readonly value: infer U;
    }[]
      ? U
      : never)
  | (T extends { readonly initialValue: readonly (infer U)[] } ? U : never),
];

type ExtractLocationRangeType<T extends LocationRangeFilter> = {
  [K in keyof T['items']]: {
    readonly from?: number;
    readonly to?: number;
  };
};

type ExtractDropdownExternalType<T extends DropdownExternalFilter> =
  | T['initialValue']
  | (T extends { readonly canClear: true } ? undefined : never);

type ExtractDropdownExternalMultipleType<T extends DropdownExternalMultipleFilter> =
  T['initialValue'];

type ExtractRangeType<T extends RangeFilter> = Pick<T, 'min' | 'max'>;

export type SearchFilterItems<TFilterItems extends SearchFilterItemsInput> = {
  readonly [TFilterItemsKey in keyof TFilterItems]: DeepReadonly<
    TFilterItems[TFilterItemsKey] extends DropdownFilter
      ? ExtractDropdownType<TFilterItems[TFilterItemsKey]>
      : TFilterItems[TFilterItemsKey] extends DropdownMultipleFilter
      ? ExtractDropdownMultipleType<TFilterItems[TFilterItemsKey]>
      : TFilterItems[TFilterItemsKey] extends DropdownExternalFilter
      ? ExtractDropdownExternalType<TFilterItems[TFilterItemsKey]>
      : TFilterItems[TFilterItemsKey] extends DropdownExternalMultipleFilter
      ? ExtractDropdownExternalMultipleType<TFilterItems[TFilterItemsKey]>
      : TFilterItems[TFilterItemsKey] extends LocationRangeFilter
      ? ExtractLocationRangeType<TFilterItems[TFilterItemsKey]>
      : TFilterItems[TFilterItemsKey] extends RangeFilter
      ? ExtractRangeType<TFilterItems[TFilterItemsKey]>
      : never
  >;
};

export const mkSearchFilters: <T extends SearchFilterItemsInput>(filters: T) => T =
  F.identity;

export const extractInitialValuesOfSearchFilter = action(
  <T extends SearchFilterItemsInput>(filters: T): SearchFilterItems<T> =>
    F.pipe(
      filters,
      RR.map(filter => {
        switch (filter.type) {
          case SearchFilterType.DropdownMultiple:
          case SearchFilterType.DropdownExternalMultiple:
            return filter.initialValue || [];
          case SearchFilterType.LocationRange:
            return F.pipe(
              filter.items,
              RR.map(
                ({ initialValue }) =>
                  initialValue || ({ from: undefined, to: undefined } as const),
              ),
            );
          case SearchFilterType.Range:
            return {
              min: filter.min,
              max: filter.max,
              ...(filter.initialValue || {}),
            } as const;
          default:
            return filter.initialValue;
        }
      }),
    ) as SearchFilterItems<T>,
);

@Component({
  selector: 'md-search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchFilterComponent<
    TFiltersInput extends SearchFilterItemsInput,
    TFilters extends SearchFilterItems<TFiltersInput> = SearchFilterItems<TFiltersInput>,
    TChangedFilter extends {
      [TChangedFilterKey in keyof TFilters]: {
        name: TChangedFilterKey;
        oldValue: TFilters[TChangedFilterKey];
        newValue: TFilters[TChangedFilterKey];
      };
    }[keyof TFilters] = {
      [TChangedFilterKey in keyof TFilters]: {
        name: TChangedFilterKey;
        oldValue: TFilters[TChangedFilterKey];
        newValue: TFilters[TChangedFilterKey];
      };
    }[keyof TFilters],
  >
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input()
  filters?: TFiltersInput;

  @Output()
  changeFilters = new EventEmitter<TFilters>();

  @Output()
  search = new EventEmitter<string>();

  @observable
  isFiltersActive = true;

  @observable
  initialFilterValues: TFilters | undefined = undefined;

  @observable
  filterValues: TFilters | undefined = undefined;

  @observable
  appliedFilterValues: TFilters | undefined = undefined;

  DistanceUtils = DistanceUtils;

  @action
  applyFilters = () => {
    if (!this.filterValues) {
      return;
    }

    this.appliedFilterValues = toJS(this.filterValues);
    this.isFiltersActive = false;
  };

  @action
  resetFilters = () => {
    if (!this.filterValues) {
      return;
    }

    this.filterValues = toJS(this.initialFilterValues);
    this.applyFilters();
  };

  @action
  toggleFiltersActive = (forceValue = !this.isFiltersActive) => {
    this.isFiltersActive = forceValue;
    if (!forceValue) {
      this.filterValues = toJS(this.appliedFilterValues);
    }
  };

  handleSearch = (value: string) => {
    this.search.emit(value);
  };

  @action
  changeFilterValue = ({ newValue, name }: Omit<TChangedFilter, 'oldValue'>) => {
    if (!this.filterValues) {
      return;
    }

    this.filterValues = {
      ...this.filterValues,
      [name]: newValue,
    };
  };

  @action
  changeLocationRangeValue = ({
    filterName,
    locationName,
    event,
    type: _type,
  }: {
    filterName: string;
    locationName: string;
    event: Event;
    type: 'from' | 'to';
  }) => {
    if (!this.filterValues || filterName in this.filterValues === false) {
      return;
    }

    const rawValue = (event.target as HTMLInputElement)?.value || '';
    const value =
      rawValue === '' ? undefined : DistanceUtils.toMetre(parseFloat(rawValue));

    const filter = this.filterValues[
      filterName
    ] as ExtractLocationRangeType<LocationRangeFilter>;

    this.filterValues = {
      ...this.filterValues,
      [filterName]: {
        ...filter,
        [locationName]: {
          ...filter[locationName],
          [_type]: value,
        },
      },
    };
  };

  avoidNegativeNumber = (event: Event) => {
    if (event instanceof KeyboardEvent && event.key === '-') {
      event.preventDefault();
    }
  };

  constructor() {
    super();
    makeObservable(this);

    this.reaction(
      () => ({
        appliedFilterValues: this.appliedFilterValues,
      }),
      ({ appliedFilterValues }) => {
        if (appliedFilterValues) {
          this.changeFilters.emit(toJS(appliedFilterValues));
        }

        this.isFiltersActive = false;
      },
      { equals: comparer.structural, fireImmediately: true },
    );
  }

  @action
  ngOnChanges(changes: SimpleChanges): void {
    if ('filters' in changes) {
      const newFilterValues = F.pipe(
        this.filters,
        O.fromNullable,
        O.fold(
          (): undefined | TFilters => undefined,
          extractInitialValuesOfSearchFilter as (input: TFiltersInput) => TFilters,
        ),
      );

      if (
        this.filters &&
        this.initialFilterValues &&
        this.filterValues &&
        this.appliedFilterValues &&
        newFilterValues
      ) {
        const initialFilterValues = this.initialFilterValues;

        this.filterValues = {
          ...newFilterValues,
          ...(F.pipe(
            this.filterValues as RR.ReadonlyRecord<string, TFilters>,
            RR.filterWithIndex(
              (name, value) => !comparer.structural(value, initialFilterValues[name]),
            ),
          ) as TFilters),
        };
      } else {
        this.filterValues = toJS(newFilterValues);
      }

      this.initialFilterValues = newFilterValues;
      this.appliedFilterValues = toJS(this.filterValues);
    }
  }

  ngOnInit(): void {}

  isDropdown(value: SearchFilters): value is DropdownFilter {
    return value.type === SearchFilterType.Dropdown;
  }

  getDropdown<T extends DropdownFilter>(value: T): T {
    return value;
  }

  isDropdownMultiple(value: SearchFilters): value is DropdownMultipleFilter {
    return value.type === SearchFilterType.DropdownMultiple;
  }

  getDropdownMultiple<T extends DropdownMultipleFilter>(value: T): T {
    return value;
  }

  isDropdownExternal(value: SearchFilters): value is DropdownExternalFilter {
    return value.type === SearchFilterType.DropdownExternal;
  }

  getDropdownExternal<T extends DropdownExternalFilter>(value: T): T {
    return value;
  }

  isDropdownExternalMultiple(
    value: SearchFilters,
  ): value is DropdownExternalMultipleFilter {
    return value.type === SearchFilterType.DropdownExternalMultiple;
  }

  getDropdownExternalMultiple<T extends DropdownExternalMultipleFilter>(value: T): T {
    return value;
  }

  isLocationRange(value: SearchFilters): value is LocationRangeFilter {
    return value.type === SearchFilterType.LocationRange;
  }

  getLocationRange<T extends LocationRangeFilter>(value: T): T {
    return value;
  }

  isRange(value: SearchFilters): value is RangeFilter {
    return value.type === SearchFilterType.Range;
  }

  getRange<T extends RangeFilter>(value: T): T {
    return value;
  }

  getLocationRangeItems(
    locationRange: LocationRangeFilter,
  ): readonly { key: string; value: LocationRangeFilterItem }[] {
    return Object.keys(locationRange.items).map(
      key => ({ key, value: locationRange.items[key] } as const),
    );
  }

  getFiltersList() {
    if (!this.filters) {
      return [];
    }

    const filters = this.filters;

    return Object.keys(filters).map(key => ({ key, value: filters[key] } as const));
  }

  max(a: number, b: number): number {
    return Math.max(a, b);
  }

  min(a: number, b: number): number {
    return Math.min(a, b);
  }
}
